import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Wifi, WifiOff, Loader, Globe, AlertTriangle, Activity, ExternalLink } from 'lucide-react';
import fetchWithTenant from '../utils/fetchWithTenant';
import '../styles/v2/pages/md-device-proxy.css';

// Proxy domain is injected at build time via VITE_PROXY_DOMAIN so white-label
// instances can point at their own domain. Falls back to the hostname-substring
// behavior (this platform's own domains) when unset, so local/dev is unchanged.
const PROXY_DOMAIN = import.meta.env.VITE_PROXY_DOMAIN || (window.location.hostname.includes('dev.') ? 'proxy-dev.serverliccm.com' : 'proxy.serverliccm.com');

function generateProxyUrl(agentId, deviceIp, port) {
  const prefix = agentId.substring(0, 8);
  const ipDashed = deviceIp.replace(/\./g, '-');
  return `https://${prefix}-${ipDashed}-${port}.${PROXY_DOMAIN}`;
}

export default function MdDeviceProxy() {
  const { agentId, deviceIp, port } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const deviceName = searchParams.get('name') || `${deviceIp}:${port}`;
  const returnPath = searchParams.get('return') || '/monitoring/devices';

  const [tunnelStatus, setTunnelStatus] = useState('checking');

  const checkTunnel = useCallback(async () => {
    try {
      const res = await fetchWithTenant(`/api/md/proxy/${agentId}/status`);
      if (res.ok) {
        const data = await res.json();
        setTunnelStatus(data.tunnelConnected ? 'connected' : 'disconnected');
      } else {
        setTunnelStatus('error');
      }
    } catch {
      setTunnelStatus('error');
    }
  }, [agentId]);

  useEffect(() => {
    checkTunnel();
    const interval = setInterval(checkTunnel, 30000);
    return () => clearInterval(interval);
  }, [checkTunnel]);

  const statusConfig = {
    checking: { color: '#ffaa00', icon: Loader, label: 'Checking tunnel...' },
    connected: { color: '#00c853', icon: Wifi, label: 'Tunnel Connected' },
    disconnected: { color: '#ff1744', icon: WifiOff, label: 'Agent Offline' },
    error: { color: '#ff6d00', icon: AlertTriangle, label: 'Connection Error' },
  };
  const status = statusConfig[tunnelStatus] || statusConfig.error;
  const StatusIcon = status.icon;

  const commonPorts = [80, 443, 8080, 8443, 7001];
  const defaultPort = parseInt(port);

  return (
    <div className="mdp-container">
      <div className="mdp-toolbar">
        <div className="mdp-toolbar-left">
          <button className="mdp-back-btn" onClick={() => navigate(returnPath)}>
            <ArrowLeft size={16} /> Back
          </button>
          <div className="mdp-device-info">
            <span className="mdp-device-name">{deviceName}</span>
            <span className="mdp-device-addr">{deviceIp}</span>
          </div>
        </div>
        <div className="mdp-status" style={{ '--status-color': status.color }}>
          <StatusIcon size={14} className={tunnelStatus === 'checking' ? 'spin' : ''} />
          <span>{status.label}</span>
        </div>
      </div>

      <div className="mdp-content">
        {tunnelStatus === 'disconnected' || tunnelStatus === 'error' ? (
          <div className="mdp-error-card">
            <WifiOff size={48} />
            <h3>Agent Not Connected</h3>
            <p>The monitoring agent needs to be running and connected to access device management interfaces.</p>
            <button className="btn btn-secondary" onClick={checkTunnel}>
              <RefreshCw size={14} /> Retry
            </button>
          </div>
        ) : tunnelStatus === 'checking' ? (
          <div className="mdp-error-card">
            <Loader size={48} className="spin" />
            <h3>Connecting...</h3>
            <p>Checking tunnel status to agent.</p>
          </div>
        ) : (
          <div className="mdp-panels">
            <div className="mdp-panel">
              <h3><Globe size={16} /> Device Web Interface</h3>
              <p className="mdp-panel-desc">
                Open the device's management interface in a new tab. 
                All traffic is securely tunneled through the monitoring agent.
              </p>

              <div className="mdp-port-grid">
                {commonPorts.map(p => {
                  const url = generateProxyUrl(agentId, deviceIp, p);
                  return (
                    
                    <a key={p}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`mdp-port-btn${p === defaultPort ? ' active' : ''}`}
                    >
                      <Globe size={18} />
                      <span className="mdp-port-label">Port {p}</span>
                      <span className="mdp-port-proto">{p === 443 || p === 8443 ? 'HTTPS' : 'HTTP'}</span>
                      <ExternalLink size={12} className="mdp-port-external" />
                    </a>
                  );
                })}
              </div>

              <div className="mdp-url-preview">
                <span className="mdp-url-label">Default URL:</span>
                <a href={generateProxyUrl(agentId, deviceIp, defaultPort)} target="_blank" rel="noopener noreferrer">
                  {generateProxyUrl(agentId, deviceIp, defaultPort)}
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
